const PAGINATION_TYPES = {
  STATIC: 'STATIC',
  LOAD_MORE: 'LOAD_MORE',
}

class Cache {
  constructor () {
    /** @type {Map<String, String>} */
    this.cache = new Map()
  }

  exists (key) {
    return this.cache.has(key)
  }

  /**
   * @param {String} key
   * @param {String} value
   */
  set (key, value) {
    this.cache.set(key, value)
  }

  /**
   * @param {String} key
   */
  get (key) {
    return this.cache.get(key)
  }

  delete (key) {
    this.cache.delete(key)
  }
}

class DataFetcher {
  constructor () {
    this.cache = new Cache()
  }

  /**
   * @param {String} url
   * @param {String} section
   */
  async fetch (url, section) {
    return this.cache.exists(url)
      ? this.cache.get(url)
      : await this.#fetchData(url, section)
  }

  /**
   * @param {String} url
   * @param {String} section
   *
   * @returns {Promise<String>}
   */
  async #fetchData (url, section) {
    const html = await window.BAO.sectionRenderer.fetchSingle(section, url)

    this.cache.set(url, html)

    return html
  }
}

class BasePaginationRenderer {
  /**
   * @param {CollectionFilters} element
   */
  constructor (element) {
    this.element = element
  }

  /**
   * @param {Document} document
   * @param {SubmitEvent} event
   *
   * @returns {void}
   */
  render (document, event) {
    this.renderProductGrid(document, event)
  }

  /**
   * @param {Document} document
   * @param {SubmitEvent} event
   *
   * @returns {void}
   */
  renderProductGrid (document, event) {
    this.element.throwError(`${this.constructor.name}.render not implemented`)
  }
}

class StaticPaginationRenderer extends BasePaginationRenderer {
  renderProductGrid (document) {
    const productGrid = this.element.els['product-grid'].element
    if (!productGrid) return

    this.element.log(`${this.constructor.name}.renderProductGrid`, {
      productGrid,
      document,
    })

    productGrid.innerHTML = window.BAO.Utils.getElement(
      this.element.els['product-grid'].selector,
      { context: document },
    ).innerHTML
  }
}

class LoadMorePaginationRenderer extends BasePaginationRenderer {
  renderProductGrid (html, e) {
    const productGrid = this.element.els['product-grid'].element
    if (!productGrid) return

    const direction = this.getPaginationDirection(e)
    const newItems = document.createDocumentFragment()
    const newElements = window.BAO.Utils.getElements(
      `[${this.element._elIdentifier}="item"]`,
      {
        context: html,
      },
    )

    newElements.forEach((el) => newItems.appendChild(el))

    if (direction == null) {
      this.element.els['item-container'].element.innerHTML = ''
      this.element.els['item-container'].element.append(newItems)
    } else if (direction === 'PREVIOUS') {
      this.element.els['item-container'].element.prepend(newItems)
    } else if (direction === 'NEXT') {
      this.element.els['item-container'].element.append(newItems)
    }

    this.renderLoadPrevious(html)
    this.renderLoadMore(html)

    document
      .querySelectorAll(`[${this.element._elIdentifier}="item"] img[srcset]`)
      .forEach((image) => {
        // eslint-disable-next-line no-self-assign
        image.outerHTML = image.outerHTML
      })

    document
      .querySelector(`[${this.element._elIdentifier}="productpanel"]`)
      ?.setAttribute('aria-hidden', (newElements.length === 0).toString())
    document
      .querySelector(`[${this.element._elIdentifier}="emptypanel"]`)
      ?.setAttribute('aria-hidden', (newElements.length > 0).toString())
  }

  /**
   * @param {PointerEvent} e
   *
   * @return 'NEXT'|'PREVIOUS'|null
   */
  getPaginationDirection (e) {
    if (typeof e === 'undefined') return null

    const paginationElement = e.target.closest(
      `${this.element.els['load-previous'].selector}, ${this.element.els['load-more'].selector}`,
    )

    if (!paginationElement) return null

    return paginationElement === this.element.els['load-previous']?.element
      ? 'PREVIOUS'
      : 'NEXT'
  }

  /**
   * @param {Document} html
   */
  renderLoadPrevious (html) {
    if (!this.shouldShowLoadPrevious && this.element.els['load-previous']) {
      return (this.element.els['load-previous'].element.innerHTML = '')
    }

    if (!this.shouldRerenderLoadPrevious) return

    if (this.element.els['load-previous']) {
      this.element.els['load-previous'].element.innerHTML =
        window.BAO.Utils.getElement(
          this.element.els['load-previous'].selector,
          { context: html },
        ).innerHTML
    }
  }

  /**
   * @param {Document} html
   */
  renderLoadMore (html) {
    if (!this.shouldRerenderLoadMore) return

    if (this.element.els['load-more']) {
      this.element.els['load-more'].element.innerHTML =
        window.BAO.Utils.getElement(this.element.els['load-more'].selector, {
          context: html,
        }).innerHTML
    }
  }

  get shouldShowLoadPrevious () {
    const anElementFromTheFirstPageExists = !!window.BAO.Utils.getElement(
      `${this.element.els.item?.selector}[data-page="1"]`,
    )

    if (anElementFromTheFirstPageExists) return false

    return this.currentPage > 1
  }

  get shouldRerenderLoadPrevious () {
    return this.currentPage <= this.currentLowestPage
  }

  get shouldRerenderLoadMore () {
    return this.currentPage >= this.currentHighestPage
  }

  get currentPage () {
    const params = this.element.searchParams.current
    const page = params.get('page')

    return Number(page) || 1
  }

  get currentLowestPage () {
    if (!this.element.els.item?.selector) return 0
    const productGridItems = window.BAO.Utils.getElements(
      `${this.element.els.item.selector}`,
    )

    return Number(productGridItems[0].dataset.page) || 1
  }

  get currentHighestPage () {
    if (!this.element.els.item?.selector) return 0
    const productGridItems = window.BAO.Utils.getElements(
      `${this.element.els.item.selector}`,
    )

    return (
      Number(productGridItems[productGridItems.length - 1].dataset.page) || 1
    )
  }
}

class Renderer {
  /**
   * @param {CollectionFilters} element
   */
  constructor (element) {
    this.element = element
    this.paginationRenderer = this.getPaginationRenderer()
  }

  /**
   * @param {String} html
   * @param {SubmitEvent} [event]
   *
   * @returns {void}
   */
  async render (html, event) {
    const newHTMLDom = new DOMParser().parseFromString(html, 'text/html')

    this.renderFacets(newHTMLDom, event)
    this.renderActiveFacets(newHTMLDom)
    this.paginationRenderer.render(newHTMLDom, event)

    window.yotpoWidgetsContainer?.initWidgets()

    this.renderProductCount(newHTMLDom)
    this.renderFilterDrawerTrigger(newHTMLDom)
    this.renderAdditional(newHTMLDom)

    this.element.loading = false
  }

  restoreFocus () {
    const restoreId = this.element.dataset.restoreFocusId
    if (!restoreId) return

    const newInput = this.element.querySelector(`#${CSS.escape(restoreId)}`)

    if (newInput) {
      newInput.focus()
    }

    delete this.element.dataset.restoreFocusId
  }

  /**
   * @param {Document} document
   *
   * @returns {void}
   */
  renderFacets (document) {
    const currentContainer = this.element.externalEls.facets.element
    const newContainer = window.BAO.Utils.getElement(
      this.element.externalEls.facets.selector,
      { context: document },
    )

    if (!currentContainer || !newContainer) return

    const openFacetIndexes = new Set(
      [
        ...currentContainer.querySelectorAll(
          '[data-accordion-group-el="trigger"][aria-expanded="true"]',
        ),
      ].map((trigger) => trigger.dataset.index),
    )

    const restoreFocusId = this.element.dataset.restoreFocusId

    currentContainer.innerHTML = newContainer.innerHTML

    openFacetIndexes.forEach((index) => {
      const trigger = currentContainer.querySelector(
        `[data-accordion-group-el="trigger"][data-index="${CSS.escape(index)}"]`,
      )
      const contentIndex = index.replace('-trigger', '-content')
      const content = currentContainer.querySelector(
        `[data-accordion-group-el="content"][data-index="${CSS.escape(contentIndex)}"]`,
      )

      trigger?.setAttribute('aria-expanded', 'true')
      content?.setAttribute('aria-hidden', 'false')
    })

    if (restoreFocusId) {
      currentContainer.querySelector(`#${CSS.escape(restoreFocusId)}`)?.focus()
      delete this.element.dataset.restoreFocusId
    }
  }

  /**
   * @param {Document} document
   *
   * @returns {void}
   */
  renderActiveFacets (document) {
    const activeFacets = this.element.externalEls['active-facets'].elements
    if (activeFacets.length <= 0) return

    activeFacets.forEach((activeFacet) => {
      const selectorParts = {
        class: `.${Array.from(activeFacet.classList).join('.')}`,
        id: `#${activeFacet.id}`,
        additional: `${this.element.externalEls['active-facets'].selector}`,
      }
      if (activeFacets.length > 1 && !activeFacet.id) {
        throw new Error('Multiple active facets found, so all require an ID')
      }
      const selector = Object.values(selectorParts).join('')
      const activeFacetElement = document.querySelector(selector)

      this.element.log('renderActiveFacets', {
        parsedHTML: document,
        activeFacetElement,
        selector,
      })

      if (!activeFacetElement) return

      const currentEl = window.BAO.Utils.getElement(selector)
      if (!currentEl) return

      const hasActiveItems = !!window.BAO.Utils.getElement(
        `[${this.element._externalElIdentifier}="active-facet"]`,
        { context: activeFacetElement },
      )

      activeFacet.dataset.empty = (!hasActiveItems).toString()

      currentEl.innerHTML = activeFacetElement.innerHTML
    })
  }

  /**
   * @param {Document} document
   *
   * @returns {void}
   */
  renderProductCount (document) {
    const existingProductCounts = window.BAO.Utils.getElements(
      this.element.els['product-count']?.selector,
    )

    const newProductCount = window.BAO.Utils.getElement(
      this.element.els['product-count']?.selector,
      { context: document },
    )
    if (!newProductCount) return

    existingProductCounts.forEach(
      (element) => (element.innerHTML = newProductCount.innerHTML),
    )
  }

  renderFilterDrawerTrigger (document) {
    const existingEl = window.BAO.Utils.getElement(
      '[data-collection-filters-el="filter-drawer"]',
    )
    if (!existingEl) return

    const newEl = window.BAO.Utils.getElement(
      '[data-collection-filters-el="filter-drawer"]',
      { context: document },
    )
    if (!newEl) return

    existingEl.innerHTML = newEl.innerHTML
  }

  /**
   * @param {Document} document
   *
   * @returns {void}
   */
  renderAdditional (document) {
    const existingFiltersButtonText = window.BAO.Utils.getElement(
      '[data-mobile-filters-button-text]',
    )
    if (!existingFiltersButtonText) return

    const newFiltersButtonText = window.BAO.Utils.getElement(
      '[data-mobile-filters-button-text]',
      { context: document },
    )
    if (!newFiltersButtonText) return

    existingFiltersButtonText.innerHTML = newFiltersButtonText.innerHTML
  }

  getPaginationRenderer () {
    switch (this.paginationType) {
      case PAGINATION_TYPES.STATIC:
        return new StaticPaginationRenderer(this.element)
      case PAGINATION_TYPES.LOAD_MORE:
        return new LoadMorePaginationRenderer(this.element)
      default:
        return new StaticPaginationRenderer(this.element)
    }
  }

  /**
   * @returns {'STATIC'|'LOAD_MORE'}
   */
  get paginationType () {
    if (!this.element.dataset.paginationType) return PAGINATION_TYPES.STATIC

    return this.element.dataset.paginationType
  }
}

export class CollectionFilters extends window.BAO.CustomElement() {
  static get styles () {
    return `:host { display: block; }`
  }

  static get requiredElements () {
    return ['product-grid']
  }

  static get requiredExternalElements () {
    return ['facets', 'facet']
  }

  fetcher = new DataFetcher()
  renderer = new Renderer(this)

  constructor () {
    super()
    /**
     * sections - a list of sections that are related to this element. They can be updated by
     * calling the renderSections method with updated HTML
     * @type {Section[]}
     * @public
     */
    this.sections = [
      {
        id: this.dataset.sectionId,
        section: this.dataset.sectionType,
        selector: '.shopify-section',
      },
    ]

    /**
     * @type {{ previous: URLSearchParams, current: URLSearchParams, initial: URLSearchParams }}
     * @public
     */
    this.searchParams = {
      // eslint-disable-next-line compat/compat
      previous: new URLSearchParams(window.location.search),
      // eslint-disable-next-line compat/compat
      current: new URLSearchParams(window.location.search),
      // eslint-disable-next-line compat/compat
      initial: new URLSearchParams(window.location.search),
    }
  }

  setupListeners () {
    super.setupListeners()

    this.onLinkClick = this.onLinkClick.bind(this)

    this.debouncedOnSubmit = window.BAO.Utils.debounce(
      (e) => this.onSubmit(e),
      500,
    )
    this.listeners.add(document, 'input', (e) => {
      if (!e.target.closest('#CollectionFiltersForm')) return
      this.debouncedOnSubmit(e)
    })
    this.listeners.add(document, 'submit', (e) => {
      if (!e.target.closest('#CollectionFiltersForm')) return
      this.onFormSubmit(e)
    })
    this.listeners.add(window, 'popstate', this.onHistoryChange.bind(this))
    this.listeners.add(this, 'click', this.onPaginationClick.bind(this))
  }

  /**
   * @param {MouseEvent} event
   */
  onLinkClick (event) {
    event.preventDefault()

    const searchParams = new URLSearchParams(event.target.search)

    this.renderPage({ searchParams: searchParams.toString() })
  }

  onSubmit (event) {
    event.preventDefault()

    const parentForm = event.target.closest('form')

    if (document.activeElement && document.activeElement.id) {
      this.dataset.restoreFocusId = document.activeElement.id
    }

    parentForm.dispatchEvent(
      new Event('submit', {
        bubbles: true,
        cancelable: true,
      }),
    )
  }

  onFormSubmit (event) {
    event.preventDefault()

    const formData = new FormData(event.target)
    const searchParams = new URLSearchParams(formData).toString()

    this.renderPage({ searchParams, event })

    if (window.BAO.MediaBreakpoints.isSm) return

    this.els['product-grid'].element.scrollIntoView({ behavior: 'smooth' })
  }

  /**
   * @param {PopStateEvent} event
   *
   * @returns {void}
   */
  onHistoryChange (event) {
    const searchParams = event.state.searchParams
    if (!searchParams) return

    if (searchParams.toString() === this.searchParams.previous.toString()) {
      return
    }

    this.renderPage({ searchParams, updateURLHash: false })
  }

  async onPaginationClick (event) {
    const pagination = event.target.closest(this.els?.pagination?.selector)
    if (!pagination) return

    const anchor = event.target.closest('a')
    this.log('onPaginationClick', { anchor })
    if (!anchor || anchor.getAttribute('href') === '' || anchor.disabled) {
      return
    }

    event.preventDefault()

    const searchParams = new URLSearchParams(anchor.search)
    const page = searchParams.get('page') ?? '1'
    const currentSearchParams = new URLSearchParams(
      this.searchParams.current.toString(),
    )
    currentSearchParams.set('page', page)

    await this.renderPage({
      searchParams: currentSearchParams.toString(),
      event,
    })
  }

  /**
   * @param {Object}
   * @param {String} searchParams
   * @param {SubmitEvent} [event]
   * @param {Boolean} [updateURLHash]
   *
   * @returns {void}
   */
  async renderPage ({ searchParams, event, updateURLHash = true } = {}) {
    this.log('renderPage', { searchParams, event, updateURLHash })

    this.searchParams.previous = this.searchParams.current
    this.searchParams.current = new URLSearchParams(searchParams)

    this.loading = true

    if (updateURLHash) this.updateURLHash(this.searchParams.current)

    for (const section of this.sections) {
      const url = `${window.location.pathname}?section_id=${
        section.section
      }&${this.searchParams.current.toString()}`
      this.log('renderPageSections', { section, url })

      const html = await this.fetcher.fetch(url, section.id)

      await this.renderer.render(html, event)
    }
  }

  /**
   * @param {URLSearchParams} searchParams
   *
   * @returns {void}
   */
  updateURLHash (searchParams) {
    history.pushState(
      { searchParams: searchParams.toString() },
      '',
      `${window.location.pathname}${
        searchParams && '?'.concat(searchParams.toString())
      }`,
    )
  }

  /**
   * @returns {Boolean}
   */
  get loading () {
    return this.getAttribute('aria-busy') === 'true'
  }

  /**
   * @param {Boolean} val
   *
   * @returns {void}
   */
  set loading (val) {
    this.setAttribute('aria-busy', val.toString())
  }
}

if (!customElements.get('collection-filters')) {
  customElements.define('collection-filters', CollectionFilters)
}
